import { getGoogleMapsApiKey, loadGoogleMaps } from "utils/googleMaps";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { buildPriorityLegendItems } from "../utils/incidents.helpers";
import { PRIORITY_COLORS } from "../models/incidents";
import IncidentPriorityLegend from "./IncidentPriorityLegend";
import {
  CLEAN_DARK_MAP_STYLE,
  CLEAN_LIGHT_MAP_STYLE,
} from "../utils/map.utils";
import { useTranslation } from "react-i18next";

const FALLBACK_CENTER = { lat: 56.9496, lng: 24.1052 };
const GALICIA_CENTER = { lat: 42.4304, lng: -8.6444 };

function makeDotIcon(color) {
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeOpacity: 1,
    strokeWeight: 2,
    scale: 7,
  };
}

const LiveIncidentsMap = ({ incidents = [], onMarkerClick, tenant }) => {
  const { t } = useTranslation();
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(new Map());
  const [missingKey, setMissingKey] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [isDark, setIsDark] = useState(() =>
    document.body.classList.contains("dark")
  );
  const defaultCenter = useMemo(
    () => (tenant === "galicia" ? GALICIA_CENTER : FALLBACK_CENTER),
    [tenant]
  );

  const legendItems = useMemo(() => buildPriorityLegendItems(), []);

  useEffect(() => {
    const apiKey = getGoogleMapsApiKey();

    if (!apiKey) {
      setMissingKey(true);
      return;
    }

    let cancelled = false;
    let resizeObserver = null;

    const markers = markersRef.current;

    const initMap = () => {
      if (!mapDivRef.current) return;

      // Google injects its own DOM into this container and React does not manage
      // those children, so they survive a remount or a hot reload. Constructing a
      // new Map over the previous one stacks two maps in the same box, which
      // renders as a collapsed strip. Start from an empty container.
      mapDivRef.current.replaceChildren();

      mapRef.current = new window.google.maps.Map(mapDivRef.current, {
        center: defaultCenter,
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: document.body.classList.contains("dark")
          ? CLEAN_DARK_MAP_STYLE
          : CLEAN_LIGHT_MAP_STYLE,
      });

      setMapReady(true);

      resizeObserver = new ResizeObserver(() => {
        const map = mapRef.current;
        if (!map) return;
        window.google.maps.event.trigger(map, "resize");
        map.setCenter(defaultCenter);
      });

      resizeObserver.observe(mapDivRef.current);

      // The container can settle to its final height in the same frame the map
      // is created; without this the map keeps the size it measured first.
      requestAnimationFrame(() => {
        if (!mapRef.current) return;
        window.google.maps.event.trigger(mapRef.current, "resize");
        mapRef.current.setCenter(defaultCenter);
      });
    };

    // Two-argument then, not .then().catch(): the rejection handler must apply
    // only to the loader. Chaining .catch() after .then() would also swallow any
    // exception thrown inside initMap, hiding a real bug behind a misleading
    // "failed to load" message.
    loadGoogleMaps(apiKey).then(
      () => {
        // The component may have unmounted while Maps was loading.
        if (!cancelled) initMap();
      },
      () => {
        if (!cancelled) setLoadFailed(true);
      }
    );

    return () => {
      cancelled = true;
      if (resizeObserver) resizeObserver.disconnect();

      for (const marker of markers.values()) {
        marker.setMap(null);
      }
      markers.clear();

      mapRef.current = null;
    };
  }, [defaultCenter]);

  useEffect(() => {
    const update = () => setIsDark(document.body.classList.contains("dark"));
    update();

    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    map.setOptions({
      styles: isDark ? CLEAN_DARK_MAP_STYLE : CLEAN_LIGHT_MAP_STYLE,
    });
  }, [isDark]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || !(window.google && window.google.maps))
      return;

    const map = mapRef.current;
    const markers = markersRef.current;

    for (const inc of incidents) {
      const existing = markers.get(inc.id);
      const color = PRIORITY_COLORS[inc.priority] || "#9ca3af";

      if (!existing) {
        const marker = new window.google.maps.Marker({
          map,
          position: { lat: inc.latitude, lng: inc.longitude },
          icon: makeDotIcon(color),
          title: inc.id,
        });

        marker.addListener("click", () => onMarkerClick?.(inc.id));
        markers.set(inc.id, marker);
      } else {
        existing.setPosition({ lat: inc.latitude, lng: inc.longitude });
        existing.setIcon(makeDotIcon(color));
        existing.setMap(map);
      }
    }

    if (incidents.length === 1) {
      map.setCenter({ lat: incidents[0].latitude, lng: incidents[0].longitude });
      map.setZoom(13);
    } else if (incidents.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      incidents.forEach((inc) =>
        bounds.extend({ lat: inc.latitude, lng: inc.longitude })
      );
      map.fitBounds(bounds, 80);
    } else {
      map.setCenter(defaultCenter);
      map.setZoom(12);
    }

    const ids = new Set(incidents.map((i) => i.id));
    for (const [id, marker] of markers.entries()) {
      if (!ids.has(id)) {
        marker.setMap(null);
        markers.delete(id);
      }
    }
  }, [defaultCenter, incidents, onMarkerClick, mapReady]);

  if (missingKey || loadFailed) {
    return (
      <div className="p-6">
        <div className="rounded border bg-yellow-50 p-4 text-sm">
          <strong>
            {missingKey
              ? "Google Maps API key not found."
              : "Google Maps failed to load."}
          </strong>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        width: "100%",
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      }}
    >
      <div ref={mapDivRef} className="h-full w-full" />
      <IncidentPriorityLegend
        title={t("map.legendTitle")}
        items={legendItems}
        position="bottom-left"
      />
    </div>
  );
};

export default LiveIncidentsMap;
